import React from "react";
import {Button, Skeleton, Typography} from "@mui/material";
import {Tag} from "~/components/tag/Tag";
import type {PtV2ImportRecord} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Classes.ts";
import {parseImportErrors, splitRunHistory} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Calc.ts";
import {formatDate} from "~/experiments/pages/payment-terms-v2/paymentTermsV2Format.ts";
import {usePtV2ImportsQuery} from "~/experiments/pages/payment-terms-v2/usePaymentTermsV2Queries.ts";

/** `payment_term_leakage_register v3 · dpo_harmonization_targets v2`, from the run's datasets or its versions. */
function runVersions(r: PtV2ImportRecord): string {
    const datasets = r.datasets?.length
        ? r.datasets.map(d => d.version != null ? `${d.slug} v${d.version}` : d.slug)
        : Object.entries(r.versions ?? {}).map(([slug, v]) => `${slug} v${v}`);
    return datasets.join(' · ');
}

const Run: React.FC<{r: PtV2ImportRecord}> = ({r}) => {
    const ok = r.status === 'succeeded';
    const errors = ok ? [] : parseImportErrors(r.errors);
    return <li>
        <span className="ptv2-num">{formatDate(r.uploadedAt)}</span>
        <Tag color="default" variant="outlined" label={r.kind === 'sync' ? 'Hermes sync' : 'Zip upload'}/>
        {r.kind !== 'sync' && r.sourceName && <span>{r.sourceName}</span>}
        <Tag color={ok ? 'success' : 'error'} label={ok ? 'Succeeded' : 'Failed'}/>
        {r.uploadedBy && <span className="ptv2-muted">by {r.uploadedBy}</span>}
        {ok && runVersions(r) && <span className="ptv2-muted ptv2-num ptv2-import-counts">{runVersions(r)}</span>}
        {r.skipped?.length > 0 && <span className="ptv2-muted ptv2-import-counts">Skipped: {r.skipped.join(', ')}</span>}
        {errors.length > 0 && <span className="ptv2-warn ptv2-import-counts">
            {errors.map(e => e.file ? `${e.file}: ${e.message}` : e.message).join(' · ')}
        </span>}
    </li>;
};

/** Staff only: the latest successful sync or import; earlier runs and failed attempts on request. */
export const PtV2ImportHistory: React.FC = () => {
    const importsQ = usePtV2ImportsQuery();
    const [showEarlier, setShowEarlier] = React.useState(false);
    const [showFailed, setShowFailed] = React.useState(false);
    if (importsQ.isPending) return <Skeleton variant="rounded" height={48}/>;
    if (importsQ.isError) return <Typography variant="caption" color="error">The history could not be loaded.</Typography>;
    const {latest, earlier, failed} = splitRunHistory(importsQ.data ?? []);

    return <div className="ptv2-import-history">
        <div className="ptv2-eyebrow">Latest successful sync or import</div>
        {latest
            ? <ul><Run r={latest}/></ul>
            : <Typography variant="body2" className="ptv2-muted">No successful sync or import yet.</Typography>}
        {showEarlier && earlier.length > 0 && <ul>{earlier.map(r => <Run key={r.id} r={r}/>)}</ul>}
        {showFailed && failed.length > 0 && <ul>{failed.map(r => <Run key={r.id} r={r}/>)}</ul>}
        {(earlier.length > 0 || failed.length > 0) && <div className="ptv2-import-row">
            {earlier.length > 0 && <Button size="small" variant="text" onClick={() => setShowEarlier(v => !v)}>
                {showEarlier ? 'Hide earlier runs' : `Show earlier runs (${earlier.length})`}
            </Button>}
            {failed.length > 0 && <Button size="small" variant="text" onClick={() => setShowFailed(v => !v)}>
                {showFailed ? 'Hide failed attempts' : `Show failed attempts (${failed.length})`}
            </Button>}
        </div>}
    </div>;
};
